import { useCallback, useState } from 'react';
import moment from 'moment';

import DatePickerInput from 'components/form/DatePickerInput.component';
import { useTranslation } from 'providers/GILT.provider';
import { nowAsDate, nowAsMoment } from 'utils/date/now.util';

const DATE_PICKER_SEPARATOR = '-';

const RANGE_MAP = Object.freeze({
  'this week': {
    unit: 'weeks',
    offset: 0,
    startOf: 'isoWeek',
    endOf: 'isoWeek',
  },
  'last week': {
    unit: 'weeks',
    offset: -1,
    startOf: 'isoWeek',
    endOf: 'isoWeek',
  },
  'this month': {
    unit: 'months',
    offset: 0,
    startOf: 'month',
    endOf: 'month',
  },
  'last month': {
    unit: 'months',
    offset: -1,
    startOf: 'month',
    endOf: 'month',
  },
});

const determineRange = ({ unit, offset = 0, startOf, endOf }) => ({
  fromDate: moment(nowAsMoment()).add(offset, unit).startOf(startOf),
  toDate: moment(nowAsMoment()).add(offset, unit).endOf(endOf),
});

const CashupDateByRange = ({ onRangeChange, onFromDateChange, onToDateChange }) => {
  const [fromCalendarModal, setFromCalendarModal] = useState(false);
  const [toCalendarModal, setToCalendarModal] = useState(false);
  const [fromDate, setFromDate] = useState(() => nowAsMoment().startOf('isoWeek'));
  const [toDate, setToDate] = useState(() => nowAsMoment().endOf('isoWeek'));
  const [selectedOption, setSelectedOption] = useState('this week');

  const { t } = useTranslation();

  const onSelectChange = useCallback(
    (eventTargetValue) => {
      const rangeValues = RANGE_MAP[eventTargetValue];

      if (!rangeValues) return;

      const { fromDate, toDate } = determineRange(rangeValues);

      onRangeChange(fromDate, toDate);
      setFromDate(fromDate);
      setToDate(toDate);
      setSelectedOption(eventTargetValue);
    },
    [onRangeChange],
  );

  const changeFromDate = useCallback(
    (date) => {
      onFromDateChange(date);
      setFromDate(date);
      setSelectedOption('this week');
      setFromCalendarModal(false);
    },
    [onFromDateChange],
  );

  const changeToDate = useCallback(
    (date) => {
      onToDateChange(date);
      setToDate(date);
      setSelectedOption('this week');
      setToCalendarModal(false);
    },
    [onToDateChange],
  );

  return (
    <div data-bem="CashupDateByRange" className="csh-date-wrap clearfix">
      <div className="flex-calendar-container">
        <div className="range-item select-range">
          <div className="csh-date-select">
            <div className="csh-select-wrap csh-date-divider555">
              <select value={selectedOption} onChange={(event) => onSelectChange(event.target.value)}>
                <option disabled hidden value="this week">
                  {t('cashUp.dateByRange.thisWeek')}
                </option>
                <option value="this week">{t('cashUp.dateByRange.thisWeek')}</option>
                <option value="last week">{t('cashUp.dateByRange.lastWeek')}</option>
                <option value="this month">{t('cashUp.dateByRange.thisMonth')}</option>
                <option value="last month">{t('cashUp.dateByRange.lastMonth')}</option>
              </select>
            </div>
          </div>
        </div>
        <div className="range-item">
          <label htmlFor="start-date" style={{ cursor: 'default' }}>
            <span className="dp-label">{t('cashUp.dateByRange.filter')}</span>
            <span className="header-date" style={{ cursor: 'pointer' }} role="button" tabIndex={0} onClick={() => setFromCalendarModal((v) => !v)}>
              {fromDate.format('ll')}
              <span id="start-date" className="icon-date-btn" />
            </span>
          </label>
        </div>
        <div className="range-item">
          <label htmlFor="end-date" style={{ cursor: 'default' }}>
            <span className="dp-label">{DATE_PICKER_SEPARATOR}</span>
            <span className="header-date" style={{ cursor: 'pointer' }} role="button" tabIndex={0} onClick={() => setToCalendarModal((v) => !v)}>
              {toDate.format('ll')}
              <span id="end-date" className="icon-date-btn" />
            </span>
          </label>
        </div>
      </div>

      {fromCalendarModal && (
        <div className="day-select range-from-select-cashup">
          <DatePickerInput
            tag="CashupDateByRange__from-date"
            component={DatePickerInput.CommonDatePicker}
            inline
            selected={fromDate}
            isShowing={fromCalendarModal}
            locale="en-GB"
            onOutsideClick={() => setFromCalendarModal(false)}
            onChange={(date) => changeFromDate(date)}
            maxDate={nowAsDate()}
          />
        </div>
      )}
      {toCalendarModal && (
        <div className="day-select range-to-select-cashup">
          <DatePickerInput
            tag="CashupDateByRange__to-date"
            component={DatePickerInput.CommonDatePicker}
            inline
            selected={toDate}
            isShowing={toCalendarModal}
            locale="en-GB"
            onOutsideClick={() => setToCalendarModal(false)}
            onChange={(date) => changeToDate(date)}
            maxDate={nowAsDate()}
          />
        </div>
      )}
    </div>
  );
};

export default CashupDateByRange;
